import React from 'react';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { allOptionsValue, DEBOUNCE_TIME, defaultPagination } from 'src/constants/main';
import theme from 'src/constants/styles';
import { localeStore } from 'src/mobx/localesStore';
import { IPagination } from 'src/types/main';
import { toPickerOptions } from 'src/utils/helpers';
import { debounce } from 'src/utils/perfomance';
import { getDropdownHeights } from 'src/utils/styles';
import Icon from 'src/view/components/Controls/Icon/Icon';
import PickerOption from 'src/view/components/Controls/Pickers/Picker/components/PickerOption/PickerOption';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types/index';
import SearchInput from 'src/view/components/Controls/SearchInput/SearchInput';
import Spinner from 'src/view/components/Controls/Spinner/Spinner';
import ScrollableList from 'src/view/components/ScrollableList/ScrollableList';
import logger from '~/services/logger';
import '../Picker/style.scss';

interface IProps {
  className?: string;
  inputClassName?: string;
  emptyListText?: string;
  selectedOptions: IPickerOption[];
  defaultOptions: IPickerOption[];
  onSelect: (options: IPickerOption[]) => void;
  onFetch: (page: number, searchValue: string) => Promise<IPagination<any>>;
  maxNumberOfVisibleOptions?: number;
  isSearchable?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  defaultPlaceholder?: string;
  optionsDesc?: string;
  small?: boolean; // add small media breakPoints
}

interface IState {
  pagination: IPagination<any>;
  pages: number[];
  loading: boolean;
  searchValue: string;
  isOpen: boolean;
}

export default class CustomLazyPicker extends React.Component<IProps, IState> {
  static defaultProps = {
    selectedOptions: [],
    defaultOptions: [],
  };

  state: IState = {
    pagination: defaultPagination,
    pages: [],
    searchValue: '',
    loading: false,
    isOpen: false,
  };

  async componentDidMount() {
    await this.onFetch(1);
  }

  onFetch = async (page: number = this.state.pagination.current_page + 1) => {
    try {
      this.setState({ loading: true });
      const pagination = await this.props.onFetch(page, this.state.searchValue);
      this.setState({
        pagination: { ...pagination, data: page > 1 ? [...this.state.pagination.data, ...pagination.data] : pagination.data },
        pages: page > 1 ? [...this.state.pages, page] : [page],
        loading: false,
      });
    } catch (error: any) {
      this.setState({ loading: false });
      logger.error({ error, place: 'CustomLazyPicker onFetch' });
    }
  };

  toggleOpen = () => {
    this.setState({ isOpen: !this.state.isOpen });
  };

  onSearch = (searchValue: string) => {
    this.setState({ searchValue }, this.search);
  };

  searchHandler = async () => {
    if (!this.state.searchValue.length || this.state.searchValue.length >= 1) await this.onFetch(1);
  };
  search = debounce(this.searchHandler, DEBOUNCE_TIME.MEDIUM);

  onSelect = (option: IPickerOption) => {
    let selectedOptions: IPickerOption[];

    if (this.props.isMulti) {
      if (this.props.selectedOptions.some((item) => item.value === option.value)) {
        selectedOptions = this.props.selectedOptions.filter((item) => item.value !== option.value);
      } else if (option.value === allOptionsValue) {
        selectedOptions = [option];
      } else {
        selectedOptions = this.props.selectedOptions.concat(option).filter((_option) => _option.value !== allOptionsValue);
      }
    } else {
      if (this.props.selectedOptions.some((item) => item.value === option.value)) {
        selectedOptions = [];
      } else {
        selectedOptions = [option];
      }
      this.toggleOpen();
    }

    if (!selectedOptions.length && this.props.defaultOptions) {
      selectedOptions = this.props.defaultOptions;
    }

    this.props.onSelect(selectedOptions);
    this.setState({ searchValue: '' });
  };

  renderItem = (item: IPickerOption | undefined, index: number) => {
    if (!item) return null;
    const selectedOptions = this.props.selectedOptions || this.props.defaultOptions;

    return (
      <PickerOption
        key={index}
        item={{ label: localeStore.t(item.label), value: item.value, data: item?.data }}
        isSelected={selectedOptions.some((option) => option.value === item.value)}
        onClick={this.onSelect}
      />
    );
  };

  get placeholder(): string {
    if (!this.props.selectedOptions.length) {
      return this.props.defaultPlaceholder
        ? localeStore.t(this.props.defaultPlaceholder)
        : localeStore.t(`select_${this.props.optionsDesc || 'item'}`);
    }
    if (this.props.isMulti) {
      const selectedAllOption = this.props.selectedOptions.find((option) => option.value === allOptionsValue);
      return selectedAllOption
        ? localeStore.t(selectedAllOption.label)
        : `${localeStore.t(`selected_${this.props.optionsDesc || 'items'}`)} (${this.props.selectedOptions.length})`;
    }
    return this.props.selectedOptions[0].label.toString();
  }

  get optionsList(): IPickerOption[] {
    let list: IPickerOption[] = [...(this.state.searchValue ? [] : this.props.selectedOptions), ...toPickerOptions(this.state.pagination.data)];

    // prepare default options if pagination is not empty
    if (this.state.pagination.data.length > 0) {
      list = [...this.props.defaultOptions, ...list];
    }

    // remove duplicate options to avoid collision
    return [...new Map(list.map((opt) => [opt.value, opt])).values()];
  }

  get className(): string {
    let className: string = '';
    if (this.props.className) className += ` ${this.props.className}`;
    if (this.props.small) className += ' custom-picker_from-media';

    return className;
  }

  get listStyles(): React.CSSProperties {
    return getDropdownHeights(this.optionsList.length, this.props.isSearchable, this.props.maxNumberOfVisibleOptions);
  }

  render() {
    return (
      <div className={`d-flex flex-row pos-relative ${this.className}`}>
        <div className={`custom-picker__open-input ${this.props.inputClassName || ''}`}>
          <div
            className="custom-picker__placeholder-wrapper-with-reset d-flex align-items-center full-height full-width pl-4"
            onClick={this.toggleOpen}
          >
            <p className="custom-picker__placeholder">{this.placeholder}</p>
          </div>
          <div className="d-flex flex-row full-height align-items-center">
            {this.props.isClearable && this.props.selectedOptions.length && !this.state.loading ? (
              <button
                className={`custom-picker__icon full-height p-1 ${this.state.isOpen ? 'custom-picker__icon-reset' : ''}`}
                onClick={() => this.props.onSelect([])}
              >
                <Icon name={Icons.CloseSmall} size={16} color={theme.colors.gray2} />
              </button>
            ) : null}
            <div className="d-flex full-height align-items-center" onClick={this.toggleOpen}>
              {this.state.loading ? (
                <Spinner color={theme.colors.gray} className="pr-2" />
              ) : (
                <Icon name={Icons.Chevron} size={20} color={theme.colors.gray2} className="custom-picker__icon" />
              )}
            </div>
          </div>
        </div>
        {this.state.isOpen ? (
          <>
            <ul className="custom-picker__wrapper d-flex flex-column full-width full-height" style={this.listStyles}>
              {this.props.isSearchable ? (
                <PickerOption optionClassName="picker-option__search">
                  <SearchInput
                    withResetIcon
                    customPlaceholder
                    onChangeText={this.onSearch}
                    value={this.state.searchValue}
                    placeholder="txt_search"
                    customPlaceholderIcon={Icons.Search}
                    inputClassName="custom-picker__search-input"
                    customPlaceholderTextClassName="custom-picker__search-text"
                  />
                </PickerOption>
              ) : null}

              <ScrollableList<IPickerOption>
                autoFetch
                scrollToTop={false}
                emptyListInFooter={false}
                fetch={this.onFetch}
                list={this.optionsList}
                loading={this.state.loading}
                refreshing={this.state.loading}
                current_page={this.state.pagination.current_page}
                last_page={this.state.pagination.last_page}
                renderItem={(item, index) => this.renderItem(item, index)}
                pages={this.state.pages}
                emptyListText={this.props.emptyListText ?? `${this.props.optionsDesc || 'items'}_not_found`}
                emptyListTextClassName="custom-picker__empty-list"
                scrollbarClassName="orders-list__scrollbar"
                scrollbar
                scrollbarSize="small"
              />
            </ul>
            <div className="custom-picker__overlay" onClick={this.toggleOpen} />
          </>
        ) : null}
      </div>
    );
  }
}
